const ACTIVE_CONVERSATION_STORAGE_PREFIX = "justiniano:active-conversation";
const DEFAULT_RETRY_DELAYS = [300, 1_000, 2_500];

function availableStorage(storage) {
  if (storage !== undefined) return storage;
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function storageKey(appId) {
  return `${ACTIVE_CONVERSATION_STORAGE_PREFIX}:${appId}`;
}

function normalizedConversationId(value) {
  const normalized = String(value || "").trim();
  return normalized && normalized.length <= 200 ? normalized : null;
}

export function readActiveConversationId(appId, { storage } = {}) {
  const resolvedStorage = availableStorage(storage);
  if (!resolvedStorage || !appId) return null;
  try {
    return normalizedConversationId(
      resolvedStorage.getItem(storageKey(appId)),
    );
  } catch {
    return null;
  }
}

export function persistActiveConversationId(appId, conversationId, {
  storage,
} = {}) {
  const normalized = normalizedConversationId(conversationId);
  const resolvedStorage = availableStorage(storage);
  if (!resolvedStorage || !appId || !normalized) return normalized;
  try {
    resolvedStorage.setItem(storageKey(appId), normalized);
  } catch {
    // Server-side restoration still works when browser storage is unavailable.
  }
  return normalized;
}

export function clearActiveConversationId(appId, { storage } = {}) {
  const resolvedStorage = availableStorage(storage);
  if (!resolvedStorage || !appId) return;
  try {
    resolvedStorage.removeItem(storageKey(appId));
  } catch {
    // Nothing else is required when browser storage is unavailable.
  }
}

function abortError() {
  const error = new Error("Conversation restoration was cancelled.");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

function waitForRetry(delay, signal) {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function retryableRestoreError(error) {
  const status = Number(error?.status);
  return !Number.isFinite(status)
    || status === 401
    || status === 408
    || status === 425
    || status === 429
    || status >= 500;
}

async function restoreOnce(
  client,
  preferredConversationId,
  signal,
  restoreLatest,
) {
  throwIfAborted(signal);
  if (preferredConversationId) {
    try {
      const restored = await client.loadConversation(
        preferredConversationId,
        { signal },
      );
      throwIfAborted(signal);
      return restored;
    } catch (error) {
      if (![403, 404].includes(Number(error?.status))) throw error;
    }
  }

  if (!restoreLatest) return null;

  const conversations = await client.listConversations({ signal });
  throwIfAborted(signal);
  const latest = conversations?.[0];
  if (!latest) return null;
  const restored = await client.loadConversation(latest.id, { signal });
  throwIfAborted(signal);
  return restored;
}

export async function restoreJustinianoConversation(client, {
  preferredConversationId = null,
  retryDelays = DEFAULT_RETRY_DELAYS,
  signal,
  wait = waitForRetry,
  restoreLatest = true,
} = {}) {
  let attempt = 0;
  while (true) {
    try {
      return await restoreOnce(
        client,
        preferredConversationId,
        signal,
        restoreLatest,
      );
    } catch (error) {
      throwIfAborted(signal);
      if (
        attempt >= retryDelays.length
        || !retryableRestoreError(error)
      ) {
        throw error;
      }
      await wait(retryDelays[attempt], signal);
      attempt += 1;
    }
  }
}
